import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Wordmark } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";

export default function ResetPassword() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (form.password !== form.confirm) {
      toast.error("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password: form.password });
      toast.success("Password reset. Please sign in.");
      nav("/auth", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "This reset link is invalid or has expired.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="bp-noise flex min-h-screen items-center justify-center px-4 py-10">
        <div className="w-full max-w-md rounded-3xl border border-[#E8E4DF] bg-white p-7 text-center bp-card-shadow">
          <p className="text-sm text-[#44413E]">This reset link is missing its token.</p>
          <Button onClick={() => nav("/forgot-password")} data-testid="reset-request-new"
            className="mt-4 h-11 w-full rounded-full bg-[#F36353] text-white hover:bg-[#D94A3D]">
            Request a new link
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bp-noise flex min-h-screen items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <button onClick={() => nav("/")} className="mb-8 flex justify-center" data-testid="reset-logo">
          <Wordmark />
        </button>
        <div className="rounded-3xl border border-[#E8E4DF] bg-white p-7 bp-card-shadow bp-fade-up">
          <h1 className="font-display text-2xl font-semibold tracking-tight text-[#0F3D66]">
            Set a new password
          </h1>
          <p className="mt-1 text-sm text-[#6B6763]">Choose a password with at least 8 characters.</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="password" className="text-[#0F3D66]">New password</Label>
              <Input id="password" type="password" required data-testid="reset-password-input" value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1 rounded-xl border-[#E8E4DF] focus:border-[#F36353] focus-visible:ring-[#F36353]/20"
                placeholder="••••••••" />
            </div>
            <div>
              <Label htmlFor="confirm" className="text-[#0F3D66]">Confirm password</Label>
              <Input id="confirm" type="password" required data-testid="reset-confirm-input" value={form.confirm}
                onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                className="mt-1 rounded-xl border-[#E8E4DF] focus:border-[#F36353] focus-visible:ring-[#F36353]/20"
                placeholder="••••••••" />
            </div>
            <Button type="submit" disabled={loading} data-testid="reset-submit-btn"
              className="h-11 w-full rounded-full bg-[#F36353] text-white hover:bg-[#D94A3D]">
              {loading ? "Please wait…" : "Reset password"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
